/**
 * Shared Catmull-Rom → cubic Bézier control-span math (S9b).
 *
 * The MULTILEADER `leaderType = 2` (Spline) leader draws a smooth cubic
 * curve interpolating the user-clicked vertices. AutoCAD renders a cubic
 * B-spline; we reproduce it with a Catmull-Rom-derived cubic Bézier through
 * the same vertices (CLAUDE.md ПРИОРИТЕТ #1 — cubic, never quadratic).
 *
 * The tangent / control-point derivation is ONE truth consumed by both:
 *
 *   - the SVG renderer (`scene/renderMultileader.tsx`
 *     `catmullRomToCubicBezierPath`) — emits `C c1 c2 p2` path segments;
 *   - the WebGL line tessellation (`scene3d/batchedLayer.ts`
 *     `geometryToLineVertices` case `"multileader"`) — samples each span
 *     into a gl.LINES polyline via {@link sampleCatmullRomCubic}.
 *
 * Extracting it here keeps the two renderers byte-consistent on the curve
 * shape and satisfies the jscpd duplicate gate (the tangent math previously
 * lived only inside the SVG string builder).
 *
 * Tangent rule (matches the pre-extraction inline code):
 *
 *   T[i]   = (P[i+1] - P[i-1]) / 6           (interior)
 *   T[i+1] = (P[i+2] - P[i])   / 6           (interior)
 *   C1 = P[i]   + T[i]
 *   C2 = P[i+1] - T[i+1]
 *
 * At a boundary span the reflective tangent `(P[1] - P[0]) / 3` (start) or
 * `(P[n] - P[n-1]) / 3` (end) is substituted.
 */

import type { RawVec2 } from "./vecMath";

/** One cubic Bézier span: endpoints `p1`/`p2` with control points `c1`/`c2`. */
export interface CubicBezierSpan {
  p1: RawVec2;
  c1: RawVec2;
  c2: RawVec2;
  p2: RawVec2;
}

/**
 * Compute the cubic Bézier control spans for a Catmull-Rom curve through
 * `vertices`. Returns one span per polyline segment (`vertices.length - 1`
 * spans). Fewer than 2 vertices → no span (`[]`).
 */
export function catmullRomCubicSpans(vertices: RawVec2[]): CubicBezierSpan[] {
  const n = vertices.length;
  if (n < 2) return [];
  const spans: CubicBezierSpan[] = [];
  for (let i = 0; i < n - 1; i++) {
    const p0: RawVec2 = i === 0 ? vertices[0]! : vertices[i - 1]!;
    const p1: RawVec2 = vertices[i]!;
    const p2: RawVec2 = vertices[i + 1]!;
    const p3: RawVec2 = i + 2 < n ? vertices[i + 2]! : vertices[n - 1]!;

    let t1x: number;
    let t1y: number;
    let t2x: number;
    let t2y: number;
    if (i === 0) {
      // Reflective tangent at the start.
      t1x = (p2.x - p1.x) / 3;
      t1y = (p2.y - p1.y) / 3;
    } else {
      t1x = (p2.x - p0.x) / 6;
      t1y = (p2.y - p0.y) / 6;
    }
    if (i + 2 >= n) {
      // Reflective tangent at the end.
      t2x = (p2.x - p1.x) / 3;
      t2y = (p2.y - p1.y) / 3;
    } else {
      t2x = (p3.x - p1.x) / 6;
      t2y = (p3.y - p1.y) / 6;
    }
    spans.push({
      p1,
      c1: { x: p1.x + t1x, y: p1.y + t1y },
      c2: { x: p2.x - t2x, y: p2.y - t2y },
      p2,
    });
  }
  return spans;
}

/** Evaluate one cubic Bézier span at parameter `t ∈ [0, 1]`. */
function evalCubicBezier(span: CubicBezierSpan, t: number): RawVec2 {
  const mt = 1 - t;
  const a = mt * mt * mt;
  const b = 3 * mt * mt * t;
  const c = 3 * mt * t * t;
  const d = t * t * t;
  return {
    x: a * span.p1.x + b * span.c1.x + c * span.c2.x + d * span.p2.x,
    y: a * span.p1.y + b * span.c1.y + c * span.c2.y + d * span.p2.y,
  };
}

/**
 * Tessellate the Catmull-Rom cubic through `vertices` into a polyline of
 * points, sampling each span at `segmentsPerSpan` sub-segments. Fewer than
 * 2 vertices → the vertices returned verbatim (a single point or empty).
 */
export function sampleCatmullRomCubic(
  vertices: RawVec2[],
  segmentsPerSpan: number,
): RawVec2[] {
  if (vertices.length < 2) return vertices.map((v) => ({ x: v.x, y: v.y }));
  const seg = Math.max(1, Math.floor(segmentsPerSpan));
  const spans = catmullRomCubicSpans(vertices);
  const pts: RawVec2[] = [{ x: spans[0]!.p1.x, y: spans[0]!.p1.y }];
  for (const span of spans) {
    for (let k = 1; k <= seg; k++) {
      pts.push(evalCubicBezier(span, k / seg));
    }
  }
  return pts;
}
